import type { PortalDsBadgeVariant } from 'src/components/portal-design-system';
import type {
  PortalMetrics,
  PortalMetricsViewModel,
  PortalSubscriptionUsage,
  PortalUser,
  PortalUserStatus,
  PortalUserSubscriptionTier,
  PortalWorkspaceDetail,
  PortalWorkspaceMember,
  PortalWorkspaceSummary,
} from '../types/users.types';

export function mapPortalUser(user: PortalUser): PortalUser {
  return user;
}

export function computeNewUsersDelta(
  current: number,
  prior: number
): Pick<PortalMetricsViewModel, 'newUsersDelta' | 'newUsersDeltaDirection'> {
  if (prior === 0) {
    return {
      newUsersDelta: '— vs prior 30d',
      newUsersDeltaDirection: 'neutral',
    };
  }

  const percentChange = ((current - prior) / prior) * 100;
  const rounded = Math.round(percentChange);
  const sign = rounded > 0 ? '+' : '';
  const direction = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'neutral';

  return {
    newUsersDelta: `${sign}${rounded}% vs prior 30d`,
    newUsersDeltaDirection: direction,
  };
}

export function mapPortalMetrics(metrics: PortalMetrics): PortalMetricsViewModel {
  const delta = computeNewUsersDelta(metrics.new_users_30d, metrics.new_users_prior_30d);

  return {
    newUsersLast30d: metrics.new_users_30d,
    creativesGeneratedLast30d: metrics.creatives_generated_30d,
    brandsIngestedLast30d: metrics.brands_ingested_30d,
    guestSignupsLast30d: metrics.guest_signups_30d,
    guestAccountsTotal: metrics.guest_accounts_total,
    guestAccountsUnclaimed: metrics.guest_accounts_unclaimed,
    ...delta,
  };
}

export function formatSubscriptionLabel(
  subscription: PortalUserSubscriptionTier | null | undefined,
): string {
  if (!subscription) {
    return 'Unknown';
  }

  return subscription
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getSubscriptionBadgeVariant(
  tier: PortalUserSubscriptionTier | null | undefined,
): PortalDsBadgeVariant {
  switch (tier) {
    case 'basic':
    case 'pro':
      return 'blue';
    case 'media_partner':
      return 'amber';
    case 'internal':
      return 'gray';
    case 'free':
    default:
      return 'gray';
  }
}

export function normalizePortalSubscriptionUsage(
  usage: Partial<PortalSubscriptionUsage> | null | undefined,
  fallback?: Pick<PortalUser, 'monthly_used' | 'monthly_limit'>,
): PortalSubscriptionUsage {
  const monthlyUsed = fallback?.monthly_used ?? 0;
  const monthlyLimit = fallback?.monthly_limit ?? null;

  return {
    gen_edit_used: usage?.gen_edit_used ?? monthlyUsed,
    gen_edit_limit: usage?.gen_edit_limit !== undefined
      ? usage.gen_edit_limit
      : monthlyLimit,
    resize_used: usage?.resize_used ?? 0,
    resize_limit: usage?.resize_limit !== undefined
      ? usage.resize_limit
      : monthlyLimit,
  };
}

export function resolvePortalUserUsage(
  user: Pick<PortalUser, 'monthly_used' | 'monthly_limit' | 'usage'>,
): PortalSubscriptionUsage {
  return normalizePortalSubscriptionUsage(user.usage, user);
}

export function resolvePortalWorkspaceUsage(
  workspace: Pick<PortalWorkspaceSummary, 'monthly_used' | 'monthly_limit' | 'usage'>,
): PortalSubscriptionUsage {
  return normalizePortalSubscriptionUsage(workspace.usage, workspace);
}

export function resolveWorkspaceMemberCounts(
  workspace: Pick<PortalWorkspaceSummary, 'member_count' | 'active_member_count' | 'blocked_member_count'>,
): { active: number; blocked: number } {
  if (
    workspace.active_member_count !== undefined
    && workspace.blocked_member_count !== undefined
  ) {
    return {
      active: workspace.active_member_count,
      blocked: workspace.blocked_member_count,
    };
  }

  return {
    active: workspace.member_count,
    blocked: 0,
  };
}

export function workspaceMemberToPortalUser(
  member: PortalWorkspaceMember,
  workspace: Pick<PortalWorkspaceDetail, 'workspace_id'>,
): PortalUser {
  return {
    id: member.id,
    email: member.email,
    name: member.name,
    workspace_id: workspace.workspace_id,
    subscription: member.subscription,
    is_guest: member.is_guest,
    is_blocked: member.status === 'blocked',
    status: member.status,
    monthly_used: member.monthly_used,
    monthly_limit: member.monthly_limit,
    creative_quota_override: null,
    disable_billing_conversion: false,
    hide_conversion_modal: false,
    pending_campaigns: 0,
    created_at: '',
  };
}

export function isPaidSubscriptionTier(
  tier: PortalUserSubscriptionTier | null | undefined,
): boolean {
  return tier === 'basic' || tier === 'pro';
}

export function resolvePortalUserStatus(
  user: Pick<PortalUser, 'status' | 'is_blocked'>,
): PortalUserStatus {
  if (user.status === 'active' || user.status === 'blocked') {
    return user.status;
  }

  return user.is_blocked ? 'blocked' : 'active';
}

export function formatStatusLabel(status: PortalUser['status'] | undefined): string {
  if (status !== 'active' && status !== 'blocked') {
    return 'Unknown';
  }

  return status.charAt(0).toUpperCase() + status.slice(1);
}
